import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Paper, Chip, Button,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Divider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import ContactSupportIcon from "@mui/icons-material/ContactSupport";
import { fetchData, postData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";

const CATEGORIES = [
  "Academic",
  "Behavior",
  "Facility",
  "Transport",
  "Fee / Payment",
  "Teacher",
  "Other",
];

const STATUS_CONFIG = {
  Open:        { color: "#2196f3", bg: "#e3f2fd" },
  "In Progress": { color: "#ff9800", bg: "#fff3e0" },
  Resolved:    { color: "#4caf50", bg: "#e8f5e9" },
  Closed:      { color: "#9E9E9E", bg: "#F5F5F5" },
  Rejected:    { color: "#f44336", bg: "#fdecea" },
};

const FALLBACK_COMPLAINTS = [
  {
    complaintId: "CMP001",
    subject: "Homework load too high",
    category: "Academic",
    description: "The daily homework assigned is overwhelming for the child.",
    submittedDate: "10/03/2025",
    status: "Resolved",
  },
  {
    complaintId: "CMP002",
    subject: "Classroom AC not working",
    category: "Facility",
    description: "The air conditioning unit in Room 5A has not been working for two weeks.",
    submittedDate: "18/03/2025",
    status: "In Progress",
  },
  {
    complaintId: "CMP003",
    subject: "Bus delay in morning",
    category: "Transport",
    description: "School bus route 3 is consistently 20-30 minutes late in the morning.",
    submittedDate: "25/03/2025",
    status: "Open",
  },
];

const EMPTY_FORM = { subject: "", description: "", category: "" };

const StatusChip = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || { color: "#8F95B2", bg: "#f3f4f6" };
  return (
    <Chip
      label={status}
      size="small"
      sx={{ backgroundColor: cfg.bg, color: cfg.color, fontWeight: 600, fontSize: "11px", borderRadius: "20px" }}
    />
  );
};

const ParentComplaint = () => {
  const [complaints, setComplaints] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const loadComplaints = useCallback(async () => {
    try {
      const res = await fetchData("parent/complaint");
      if (res?.data) {
        const data = Array.isArray(res.data) ? res.data : res.data.data || [];
        setComplaints(data.length > 0 ? data : FALLBACK_COMPLAINTS);
      } else {
        setComplaints(FALLBACK_COMPLAINTS);
      }
    } catch (_) {
      setComplaints(FALLBACK_COMPLAINTS);
    }
  }, []);

  useEffect(() => { loadComplaints(); }, [loadComplaints]);

  const handleOpenDialog = () => {
    setFormData(EMPTY_FORM);
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setFormData(EMPTY_FORM);
    setFormErrors({});
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const errors = {};
    if (!formData.subject.trim()) errors.subject = "Subject is required";
    if (!formData.description.trim()) errors.description = "Description is required";
    if (!formData.category) errors.category = "Category is required";
    if (formData.subject.trim().length < 5) errors.subject = "Subject must be at least 5 characters";
    if (formData.description.trim().length < 10) errors.description = "Description must be at least 10 characters";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const res = await postData("parent/complaint", formData);
      const newComplaint = {
        complaintId: res?.data?.complaintId || `CMP${String(complaints.length + 1).padStart(3, "0")}`,
        subject: formData.subject,
        category: formData.category,
        description: formData.description,
        submittedDate: new Date().toLocaleDateString("en-GB").replace(/\//g, "/"),
        status: "Open",
      };
      setComplaints((prev) => [newComplaint, ...prev]);
      setToast({ open: true, severity: "success", message: "Complaint submitted successfully." });
      handleCloseDialog();
    } catch (_) {
      // Optimistic update on error
      const newComplaint = {
        complaintId: `CMP${String(complaints.length + 1).padStart(3, "0")}`,
        subject: formData.subject,
        category: formData.category,
        description: formData.description,
        submittedDate: new Date().toLocaleDateString("en-GB"),
        status: "Open",
      };
      setComplaints((prev) => [newComplaint, ...prev]);
      setToast({ open: true, severity: "warning", message: "Complaint saved locally. Will sync when online." });
      handleCloseDialog();
    } finally {
      setSubmitting(false);
    }
  };

  const openCount = complaints.filter((c) => c.status === "Open" || c.status === "In Progress").length;

  return (
    <Box sx={{ textAlign: "left", padding: "1rem", fontFamily: "'Inter', 'Roboto', sans-serif" }}>
      <Toast
        open={toast.open}
        handleClose={() => setToast({ ...toast, open: false })}
        severity={toast.severity}
        message={toast.message}
      />

      {/* New Complaint Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "10px",
                background: "linear-gradient(135deg, #244C77 0%, #4A7BA8 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ContactSupportIcon sx={{ fontSize: "18px", color: "#fff" }} />
            </Box>
            <Typography sx={{ fontWeight: "bold", fontSize: "17px" }}>New Complaint</Typography>
          </Box>
          <IconButton size="small" onClick={handleCloseDialog} sx={{ color: "#6B7280" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2.5 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            {/* Category */}
            <FormControl fullWidth size="small" error={!!formErrors.category}>
              <InputLabel sx={{ fontSize: "13px" }}>Category *</InputLabel>
              <Select
                name="category"
                value={formData.category}
                label="Category *"
                onChange={handleChange}
                sx={{ fontSize: "13px", borderRadius: "10px" }}
              >
                {CATEGORIES.map((cat) => (
                  <MenuItem key={cat} value={cat} sx={{ fontSize: "13px" }}>{cat}</MenuItem>
                ))}
              </Select>
              {formErrors.category && (
                <Typography sx={{ fontSize: "11px", color: "#f44336", mt: 0.5, ml: 1.5 }}>{formErrors.category}</Typography>
              )}
            </FormControl>

            {/* Subject */}
            <Box>
              <TextField
                fullWidth
                size="small"
                label="Subject *"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                error={!!formErrors.subject}
                inputProps={{ maxLength: 120 }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    fontSize: "13px",
                    "& fieldset": { borderColor: "#D5D7DA" },
                    "&:hover fieldset": { borderColor: "#4A7BA8" },
                    "&.Mui-focused fieldset": { borderColor: "#244C77" },
                  },
                  "& .MuiInputLabel-root": { fontSize: "13px" },
                }}
              />
              {formErrors.subject && (
                <Typography sx={{ fontSize: "11px", color: "#f44336", mt: 0.5, ml: 1.5 }}>{formErrors.subject}</Typography>
              )}
            </Box>

            {/* Description */}
            <Box>
              <TextField
                fullWidth
                size="small"
                label="Description *"
                name="description"
                value={formData.description}
                onChange={handleChange}
                error={!!formErrors.description}
                multiline
                rows={4}
                inputProps={{ maxLength: 1000 }}
                placeholder="Please describe your complaint in detail..."
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    fontSize: "13px",
                    "& fieldset": { borderColor: "#D5D7DA" },
                    "&:hover fieldset": { borderColor: "#4A7BA8" },
                    "&.Mui-focused fieldset": { borderColor: "#244C77" },
                  },
                  "& .MuiInputLabel-root": { fontSize: "13px" },
                }}
              />
              {formErrors.description && (
                <Typography sx={{ fontSize: "11px", color: "#f44336", mt: 0.5, ml: 1.5 }}>{formErrors.description}</Typography>
              )}
              <Typography sx={{ fontSize: "11px", color: "#9CA3AF", mt: 0.5, textAlign: "right" }}>
                {formData.description.length}/1000
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={handleCloseDialog}
            disabled={submitting}
            sx={{ border: "1px solid #D5D7DA", borderRadius: "8px", color: "#374151", textTransform: "none", fontSize: "13px", px: 2.5 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            sx={{
              background: "linear-gradient(90deg, #4A7BA8, #244C77)",
              color: "#fff",
              borderRadius: "8px",
              textTransform: "none",
              fontSize: "13px",
              px: 3,
              "&:hover": { background: "linear-gradient(90deg, #4A7BA8, #244C77)" },
              "&.Mui-disabled": { background: "#E5E7EB", color: "#9CA3AF" },
            }}
          >
            {submitting ? "Submitting..." : "Submit Complaint"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Page header */}
      <Typography sx={{ color: "rgba(0,0,0,0.45)", fontWeight: "bold", fontSize: "14px", mb: 1.5 }}>
        Pages / Complaints
      </Typography>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography sx={{ fontWeight: "bold", fontSize: "22px", color: "#1a1a2e" }}>
              My Complaints
            </Typography>
            <Chip
              label={`${complaints.length} Total`}
              size="small"
              sx={{ backgroundColor: "#EDE9FB", color: "#244C77", fontWeight: 600, fontSize: "11px" }}
            />
            {openCount > 0 && (
              <Chip
                label={`${openCount} Active`}
                size="small"
                sx={{ backgroundColor: "#fff3e0", color: "#ff9800", fontWeight: 600, fontSize: "11px" }}
              />
            )}
          </Box>
          <Typography sx={{ color: "#8F95B2", fontSize: "13px", mt: 0.3 }}>
            Raise and track complaints on behalf of your child
          </Typography>
        </Box>
        <Button
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
          sx={{
            background: "linear-gradient(90deg, #4A7BA8, #244C77)",
            color: "#fff",
            fontSize: "13px",
            textTransform: "none",
            px: 2.5,
            py: 1,
            borderRadius: "8px",
            "&:hover": { background: "linear-gradient(90deg, #4A7BA8, #244C77)" },
          }}
        >
          New Complaint
        </Button>
      </Box>

      {/* Complaints Table */}
      <Paper elevation={0} sx={{ borderRadius: "16px", overflow: "hidden", boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
        <Box sx={{ background: "linear-gradient(135deg, #244C77 0%, #4A7BA8 100%)", px: 3, py: 2 }}>
          <Typography sx={{ fontWeight: "bold", fontSize: "15px", color: "#fff" }}>
            Complaint History
          </Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#F9FAFB" }}>
                {["ID", "Subject", "Category", "Submitted On", "Status"].map((h) => (
                  <TableCell
                    key={h}
                    sx={{
                      fontWeight: 600,
                      fontSize: "12px",
                      color: "#6B7280",
                      py: 1.5,
                      borderBottom: "1px solid #E5E7EB",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {complaints.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <Box sx={{ textAlign: "center" }}>
                      <ContactSupportIcon sx={{ fontSize: "40px", color: "#E5E7EB", mb: 1 }} />
                      <Typography sx={{ fontSize: "14px", color: "#9CA3AF" }}>No complaints submitted yet</Typography>
                      <Button
                        onClick={handleOpenDialog}
                        sx={{ mt: 1.5, textTransform: "none", color: "#244C77", fontSize: "13px" }}
                      >
                        Raise your first complaint
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                complaints.map((row, idx) => (
                  <TableRow
                    key={row.complaintId || idx}
                    sx={{ "&:hover": { backgroundColor: "#FAFAFA" }, "&:last-child td": { borderBottom: 0 } }}
                  >
                    <TableCell sx={{ fontSize: "13px", color: "#6B7280", py: 1.4, fontFamily: "monospace" }}>
                      {row.complaintId || `CMP${String(idx + 1).padStart(3, "0")}`}
                    </TableCell>
                    <TableCell sx={{ py: 1.4 }}>
                      <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "#111827" }}>{row.subject || "—"}</Typography>
                      {row.description && (
                        <Typography sx={{ fontSize: "11px", color: "#9CA3AF", mt: 0.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 240 }}>
                          {row.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 1.4 }}>
                      <Chip
                        label={row.category || "—"}
                        size="small"
                        sx={{ backgroundColor: "#F3F4F6", color: "#374151", fontSize: "11px", fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: "13px", color: "#6B7280", py: 1.4 }}>
                      {row.submittedDate || row.createdAt
                        ? row.submittedDate || new Date(row.createdAt).toLocaleDateString("en-GB")
                        : "—"}
                    </TableCell>
                    <TableCell sx={{ py: 1.4 }}>
                      <StatusChip status={row.status} />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default ParentComplaint;
